import { Injectable, ViewRef, NgZone, ApplicationRef, ChangeDetectorRef } from '@angular/core';

class TimeoutListEntry {
    constructor(
        public handler: () => void,
        public id: number,
        public changeDetectorRef: ChangeDetectorRef,
        public skipChangeDetection: boolean
    ) { }
}

class TimeoutList {
    private queue: Array<TimeoutListEntry> = [];

    public peek(): TimeoutListEntry {
        if (this.queue.length == 0) return null;
        let ret = this.queue[0];
        return ret;
    }

    public pop(): TimeoutListEntry {
        if (this.queue.length == 0) return null;
        let ret = this.queue[0];
        this.queue.splice(0, 1);
        return ret;
    }

    public push(entry: TimeoutListEntry) {
        this.queue.push(entry);
    }

    public deleteById(id: number) {
        for (let i = 0; i < this.queue.length; i++) {
            if (this.queue[i].id == id) {
                this.queue.splice(i, 1);
                return;
            }
        }
    }
}

@Injectable({providedIn: 'root'})
export class TimeoutQueue {
    private timeoutQueues: { [id: number]: TimeoutList } = {};
    private timeoutHandlers: { [id: number]: number } = {};
    private timeoutAllocatedIds: { [id: number]: boolean } = {};

    constructor(private ngZone: NgZone, private applicationRef: ApplicationRef) {
    }

    public setTimeout(f: () => void, delay: any, changeDetectorRef?: ChangeDetectorRef, skipChangeDetection?: boolean): number {
        if (delay < 10) delay = 10;
        if (!this.timeoutQueues[delay]) {
            this.timeoutQueues[delay] = new TimeoutList();
        }

        if (this.timeoutHandlers[delay]) {
            clearTimeout(this.timeoutHandlers[delay]);
            this.timeoutHandlers[delay] = null;
        }

        this.ngZone.runOutsideAngular(() => {
            let handlerFunc = () => {
                let startDate = new Date();
                let entry: TimeoutListEntry = this.timeoutQueues[delay].pop();
                let shouldTriggerFullChangeDetection = false;
                while (entry) {
                    try {
                        entry.handler();
                    } catch (e) {
                        console.log(e);
                    }
                    if (!shouldTriggerFullChangeDetection && entry.changeDetectorRef) {
                        this.ngZone.run(() => {
                            if (!(entry.changeDetectorRef as ViewRef).destroyed) {
                                entry.changeDetectorRef.detectChanges();
                            }
                        });
                    } else {
                        if (!entry.skipChangeDetection) {
                            shouldTriggerFullChangeDetection = true;
                        }
                    }
                    delete this.timeoutAllocatedIds[entry.id];
                    let endDate = new Date();
                    let milliseconds = (endDate.getTime() - startDate.getTime());
                    if (milliseconds > 100) {
                        // Timeouts are taking too long - trigger change detection and reschedule
                        if (shouldTriggerFullChangeDetection) {
                            this.applicationRef.tick();
                        }
                        this.timeoutHandlers[delay] = setTimeout(handlerFunc, delay);
                        return;
                    }

                    entry = this.timeoutQueues[delay].pop();
                }

                this.timeoutHandlers[delay] = null;
                if (shouldTriggerFullChangeDetection) {
                    this.ngZone.run(() => {
                        this.applicationRef.tick();
                    });
                }
            };

            this.timeoutHandlers[delay] = setTimeout(handlerFunc, delay);
        });

        let id = 0;
        do {
            id = Math.floor(Math.random() * 2000000000);
        } while (this.timeoutAllocatedIds[id]);
        this.timeoutAllocatedIds[id] = true;
        this.timeoutQueues[delay].push(new TimeoutListEntry(f, id, changeDetectorRef, skipChangeDetection));
        return id;
    }

    public clearTimeout(id: number) {
        delete this.timeoutAllocatedIds[id];
        for (let delay in this.timeoutQueues) {
            this.timeoutQueues[delay].deleteById(id);
        }
    }

}
